import React from "react";
import {
  Drawer,
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  styled,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  ConfirmationNumber as TicketIcon,
  Assessment as ReportsIcon,
  AddLocationAlt as AddLocationAltIcon,
  Business as BusinessIcon,
  ExitToApp,
  Close as CloseIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import PeopleIcon from "@mui/icons-material/People";
import mdlogo from "../assets/mdlogo.svg";
import Swal from "sweetalert2";
import { DRAWER_WIDTH } from "../constants/layout";

const drawerWidth = DRAWER_WIDTH;
const mobileDrawerWidth = "100%";

// Styled ListItemButton with enhanced styles
const StyledListItemButton = styled(ListItemButton)(({ theme, selected }) => ({
  borderRadius: 8,
  padding: "10px 16px",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: selected ? "#388E3C" : "#f0f0f0",
    transform: "translateX(4px)",
  },
  ...(selected && {
    backgroundColor: "#4CAF50 !important",
    color: "white !important",
    "&:hover": {
      backgroundColor: "#388E3C !important",
    },
    "& .MuiListItemIcon-root": {
      color: "white !important",
    },
  }),
  // Better touch targets for mobile
  [theme.breakpoints.down("sm")]: {
    padding: "12px 16px",
    minHeight: "56px",
  },
}));

const Sidebar = ({
  selectedItem,
  onItemSelect,
  open,
  onClose,
  role,
  onLogout,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 1500,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener("mouseenter", Swal.stopTimer);
      toast.addEventListener("mouseleave", Swal.resumeTimer);
    },
    customClass: {
      container: "swal-toast-container",
      popup: "swal-toast-popup",
    },
  });

  const styleSheet = document.createElement("style");
  styleSheet.textContent = `
      .swal-toast-container {
        z-index: 10000 !important;
      }
      .swal-toast-popup {
        font-family: 'Roboto', sans-serif;
        font-size: 14px;
        border-radius: 8px;
      }
      .priority-chip, .status-chip {
        border-radius: 16px;
        font-weight: 600;
      }
      .comment-container {
        transition: all 0.2s ease;
        '&:hover': {
          transform: 'translateY(-2px)',
          box-shadow: '0 6px 12px rgba(0,0,0,0.15)',
        }
      }
    `;
  document.head.appendChild(styleSheet);

  const getMenuItems = () => {
    switch (role) {
      case "super_admin":
        return [
          {
            text: "Dashboard",
            icon: <DashboardIcon />,
            key: "super-admin-dashboard",
          },
          {
            text: "User Management",
            icon: <PeopleIcon />,
            key: "user-management",
          },
          {
            text: "Department",
            icon: <BusinessIcon />,
            key: "department",
          },
          { text: "Outlet", icon: <AddLocationAltIcon />, key: "outlet" },
          { text: "Tickets", icon: <TicketIcon />, key: "tickets" },
          { text: "Reports", icon: <ReportsIcon />, key: "reports" },
          {
            text: "SLA Management",
            icon: <SettingsIcon />,
            key: "sla-management",
          },
        ];
      case "admin":
        return [
          {
            text: "Dashboard",
            icon: <DashboardIcon />,
            key: "admin-dashboard",
          },
          {
            text: "User Management",
            icon: <PeopleIcon />,
            key: "user-management",
          },
          {
            text: "Department",
            icon: <BusinessIcon />,
            key: "department",
          },
          { text: "Outlet", icon: <AddLocationAltIcon />, key: "outlet" },
          { text: "Tickets", icon: <TicketIcon />, key: "tickets" },
          { text: "Reports", icon: <ReportsIcon />, key: "reports" },

          // { text: "Locations", icon: <AddLocationAltIcon />, key: "locations" },
        ];
      case "standard":
        return [
          { text: "Dashboard", icon: <DashboardIcon />, key: "user-dashboard" },
          { text: "Tickets", icon: <TicketIcon />, key: "tickets" },
        ];
      case "executive":
        return [
          {
            text: "Dashboard",
            icon: <DashboardIcon />,
            key: "executive-dashboard",
          },
          { text: "Tickets", icon: <TicketIcon />, key: "tickets" },

          {
            text: "All Assigned Tickets",
            icon: <TicketIcon />,
            key: "assigned-tickets",
          },
          { text: "Reports", icon: <ReportsIcon />, key: "reports" },

          // { text: "Analytics", icon: <ReportsIcon />, key: "analytics" },
        ];
      case "department_head":
        return [
          {
            text: "Dashboard",
            icon: <DashboardIcon />,
            key: "department-head-dashboard",
          },
          {
            text: "Department Ticket",
            icon: <BusinessIcon />,
            key: "department-ticket",
          },
          { text: "Tickets", icon: <TicketIcon />, key: "tickets" },

          { text: "Reports", icon: <ReportsIcon />, key: "reports" },
        ];
      default:
        return [];
    }
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#ffffff",
        background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
        p: 1,
        overflowY: "auto", // Ensure content is scrollable if it overflows
      }}
    >
      {/* Logo Section with Close Button */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: isMobile ? "space-between" : "center",
          bgcolor: "#fff",
          mb: 2,
        }}
      >
        <Box
          sx={{
            width: 60,
            height: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={mdlogo}
            alt="McDonald's Logo"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        </Box>
        {isMobile && (
          <IconButton onClick={onClose} sx={{ color: "#424242" }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      <Divider sx={{ mb: 2, bgcolor: "#e0e0e0" }} />

      {/* Menu Items */}
      <List sx={{ flexGrow: 1 }}>
        {getMenuItems().map((item) => (
          <ListItem key={item.key} disablePadding sx={{ px: 1, mb: 0.5 }}>
            <StyledListItemButton
              selected={selectedItem === item.key}
              onClick={() => {
                onItemSelect(item.key);
                if (isMobile) onClose();
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: "#424242",
                  [theme.breakpoints.down("sm")]: { minWidth: 36 },
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  sx: {
                    fontSize: { xs: "14px", sm: "15px" },
                    fontWeight: selectedItem === item.key ? 700 : 500,
                    color: selectedItem === item.key ? "white" : "#424242",
                  },
                }}
              />
            </StyledListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ mb: 2, bgcolor: "#e0e0e0" }} />

      {/* Logout Button */}
      <Box
        sx={{
          p: 1,
          pb: 3, // Increased bottom padding to ensure button is not cut off
          boxSizing: "border-box",
        }}
      >
        <Button
          variant="contained"
          fullWidth
          startIcon={<ExitToApp />}
          onClick={onLogout}
          sx={{
            textTransform: "none",
            bgcolor: "#DA291C",
            color: "white",
            fontWeight: 600,
            py: 1.2, // Slightly reduced padding for mobile
            borderRadius: 8,
            fontSize: "14px", // Adjusted font size for mobile
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            transition: "all 0.3s ease",
            "&:hover": {
              bgcolor: "#B71C1C",
              boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
              transform: "translateY(-1px)",
            },
            // Responsive button size
            [theme.breakpoints.down("sm")]: {
              py: 1,
              fontSize: "13px", // Slightly smaller font for mobile
            },
          }}
        >
          Log Out
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Desktop Drawer */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              bgcolor: "transparent",
              height: "calc(100vh - 20px)",
              border: "1px solid #e0e0e0",
              borderRadius: "25px",
              ml: "10px",
              mt: "10px",
              mb: "10px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Mobile Drawer - Full Screen */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={open}
          onClose={onClose}
          sx={{
            "& .MuiDrawer-paper": {
              width: mobileDrawerWidth,
              boxSizing: "border-box",
              height: "100vh",
              border: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            },
          }}
          ModalProps={{
            keepMounted: true,
            BackdropProps: {
              sx: {
                backgroundColor: "rgba(0,0,0,0.5)",
              },
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};

export default Sidebar;
